import { Formik, Form } from "formik";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Checkbox, Divider, Header, Segment } from "semantic-ui-react";
import * as Yup from "yup";
import MyTextInput from "../../../app/common/form/MyTextInput";
import { updateTaskInDb } from "../../../app/firestore/firestoreService";
import { toast } from "react-toastify";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import { closeModal } from "../../../app/common/modals/modalSlice";
import MyDateInput from "../../../app/common/form/MyDateInput";
import { useMediaQuery } from "react-responsive";
import MyTextArea from "../../../app/common/form/MyTextArea";

export default function TaskEditSimple({ task, onSave }) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  const { currentUserProfile } = useSelector((state) => state.profile);

  const initialValues = {
    ...task,
    inProgress: task.inProgress || false,
    inProgressDate: task.inProgressDate || null,
    inProgressBy: task.inProgressBy || null,
    isComplete: task.status === "Complete",
    dateMarkedComplete: task.dateMarkedComplete || null,
    completedBy: task.completedBy || null,
  };

  const validationSchema = Yup.object({
    title: Yup.string().required("You must provide a name"),
  });

  return (
    <ModalWrapper size="tiny">
      <Segment clearing>
        <div className="medium horizontal margin small top margin">
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                values.start = values.end;
                await updateTaskInDb(task.id, values);
                setSubmitting(false);
                toast.success("Task successfully updated");
                if (onSave) {
                  onSave();
                }
                dispatch(closeModal({ modalType: "TaskEditSimple" }));
              } catch (error) {
                toast.error(error.message);
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting, dirty, values, setFieldValue }) => {
              const handleInProgressChange = (checked) => {
                setFieldValue("inProgress", checked);
                if (checked) {
                  setFieldValue("inProgressDate", new Date());
                  setFieldValue("inProgressBy", {
                    id: currentUserProfile?.id || currentUserProfile?.userId,
                    firstName: currentUserProfile?.firstName || "",
                    lastName: currentUserProfile?.lastName || "",
                  });
                } else {
                  setFieldValue("inProgressDate", null);
                  setFieldValue("inProgressBy", null);
                }
              };

              const handleCompleteChange = (checked) => {
                setFieldValue("isComplete", checked);
                if (checked) {
                  setFieldValue("status", "Complete");
                  setFieldValue("dateMarkedComplete", new Date());
                  setFieldValue("completedBy", {
                    id: currentUserProfile?.id || currentUserProfile?.userId,
                    firstName: currentUserProfile?.firstName || "",
                    lastName: currentUserProfile?.lastName || "",
                  });
                  // Clear in progress when marked complete
                  setFieldValue("inProgress", false);
                  setFieldValue("inProgressDate", null);
                  setFieldValue("inProgressBy", null);
                } else {
                  setFieldValue("status", "Active");
                  setFieldValue("dateMarkedComplete", null);
                  setFieldValue("completedBy", null);
                }
              };

              return (
                <Form className="ui form" autoComplete="off">
                  <Header size="large" color="blue">
                    Edit Task
                  </Header>
                  <Divider />
                  <h4 className="zero top margin tiny bottom margin">Name</h4>
                  <MyTextInput name="title" />
                  <h4 className="zero top margin tiny bottom margin">
                    Description (optional)
                  </h4>
                  <MyTextArea rows={4} name="description" />
                  <h4 className="zero top margin tiny bottom margin">
                    Deadline (optional)
                  </h4>
                  <MyDateInput
                    name="end"
                    placeholder="End date"
                    dateFormat="MMMM d, yyyy"
                  />

                  <div className="medium top margin">
                    <Checkbox
                      label="In Progress"
                      checked={values.inProgress}
                      disabled={values.isComplete}
                      onChange={(_, { checked }) => handleInProgressChange(checked)}
                    />
                    {values.inProgress && values.inProgressBy && (
                      <p style={{ fontSize: "0.9em", color: "#666", marginTop: "4px", marginLeft: "24px" }}>
                        {values.inProgressBy.firstName} {values.inProgressBy.lastName}
                        {values.inProgressDate && (
                          <> - {new Date(values.inProgressDate).toLocaleString()}</>
                        )}
                      </p>
                    )}
                  </div>

                  <div className="small top margin medium bottom margin">
                    <Checkbox
                      label="Complete"
                      checked={values.isComplete}
                      onChange={(_, { checked }) => handleCompleteChange(checked)}
                    />
                    {values.isComplete && values.completedBy && (
                      <p style={{ fontSize: "0.9em", color: "#666", marginTop: "4px", marginLeft: "24px" }}>
                        {values.completedBy.firstName} {values.completedBy.lastName}
                        {values.dateMarkedComplete && (
                          <> - {new Date(values.dateMarkedComplete).toLocaleString()}</>
                        )}
                      </p>
                    )}
                  </div>

                  <Divider className="medium top margin medium bottom margin" />
                  <div>
                    <Button
                      primary
                      loading={isSubmitting}
                      disabled={!dirty || isSubmitting}
                      type="submit"
                      floated={isMobile ? null : "right"}
                      content="Save"
                      className={isMobile ? "fluid medium bottom margin" : null}
                    />
                    <Button
                      type="button"
                      disabled={isSubmitting}
                      onClick={() => dispatch(closeModal({ modalType: "TaskEditSimple" }))}
                      floated={isMobile ? null : "right"}
                      content="Cancel"
                      className={isMobile ? "fluid medium bottom margin" : null}
                    />
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </Segment>
    </ModalWrapper>
  );
}